/**
 * useGammaSnapshot.ts — MER Algo Footprints v9.0.0 — Stage 4
 *
 * React hook for fetching the Gamma snapshot from the v9.0.0 backend.
 *
 * Stage 4 additions (v9.0.0):
 *   - Endpoint moved from /api/gamma/snapshot → /api/gamma/v2/snapshot
 *   - Response shape adapter: the v2 backend returns a different shape than
 *     the v7.1B frontend expects. mapV2ToLiveGammaSnapshot() translates
 *     between them so the existing components render unchanged.
 *
 * Polling improvements (carried over):
 *   - No fetch or polling when symbol is empty
 *   - Pauses when document/tab is hidden (visibilitychange API)
 *   - Resumes immediately when tab becomes visible again
 *   - Mild backoff (3× interval, capped at 2 min) after BACKOFF_AFTER_N
 *     consecutive NOT_WIRED responses (provider not connected)
 *   - Uses setTimeout chain instead of setInterval for safer visibility interplay
 *
 * Design rules:
 *   - Fetch failures never throw to callers
 *   - Always resolves to { loading, snapshot, error, refresh }
 *   - Purely observational — no scoring influence
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ── Snapshot shape ──────────────────────────────────────────────────────────
// This is the shape expected by the existing v7.1B components (GammaPanel,
// HedgeFundModePage, ThesisPanel). The v9.0.0 backend returns a different
// shape — we adapt it via mapV2ToLiveGammaSnapshot below.

export interface LiveGammaSnapshot {
  symbol:           string;
  available:        boolean;
  reason:           string | null;
  regime:           'POSITIVE' | 'NEGATIVE' | 'NEUTRAL' | null;
  bias:             'PIN' | 'UPSIDE_EXPANSION' | 'DOWNSIDE_EXPANSION' | 'BALANCED' | null;
  gammaScore:       number | null;
  pinScore:         number | null;
  expansionScore:   number | null;
  directionalScore: number | null;
  callWall:         number | null;
  putWall:          number | null;
  keyMagnetStrike:  number | null;
  flipZoneLow:      number | null;
  flipZoneHigh:     number | null;
  breakoutFuelUp:   number | null;
  breakoutFuelDown: number | null;
  isStale:          boolean | null;
  computedAt:       number | null;
  dataFetchedAt:    number | null;
  dataQuality:      'high' | 'medium' | 'low' | null;
  notes:            string[];
  tradeBehaviorProfile?: {
    expectedVolatility:  string;
    breakoutLikelihood:  string;
    reversionLikelihood: string;
    driftBias:           string;
    hedgingPressure:     string;
    note:                string;
  } | null;
}

export interface UseGammaSnapshotResult {
  loading:  boolean;
  snapshot: LiveGammaSnapshot | null;
  error:    string | null;
  refresh:  () => void;
}

// ── v9.0.0 response adapter ─────────────────────────────────────────────────
// The v9.0.0 /api/gamma/v2/snapshot endpoint returns:
//   { symbol, asOf, spot, posture, postureScore, postureRaw,
//     estimatedFlipZone: {low, high, confidence},
//     strongestWalls: [{strike, side, strength, netGamma, expirations, hasGreeks}],
//     voidZones:      [{strike, side, strength, netGamma}],
//     concentration:  {top3Share, top5Share, crowdedness},
//     thesisHints:    {regimeHint, gammaPosture, nearestWallAbove, nearestWallBelow,
//                      flipZoneLow, flipZoneHigh, confidence},
//     riskFlags, confidence, meta, computedAt, available, isStale, reason, ... }
//
// The frontend's LiveGammaSnapshot shape expects flat scalar fields like
// callWall, putWall, gammaScore, pinScore, etc. This adapter maps between them.

function mapV2ToLiveGammaSnapshot(v2: any): LiveGammaSnapshot {
  // Failure envelope — pass through with nulls for all positive fields
  if (v2 && v2.available === false) {
    return {
      symbol:           v2.symbol ?? '',
      available:        false,
      reason:           v2.reason ?? null,
      regime:           null, bias: null,
      gammaScore:       null, pinScore: null, expansionScore: null, directionalScore: null,
      callWall:         null, putWall: null, keyMagnetStrike: null,
      flipZoneLow:      null, flipZoneHigh: null,
      breakoutFuelUp:   null, breakoutFuelDown: null,
      isStale:          null, computedAt: null, dataFetchedAt: null,
      dataQuality:      null, notes: [],
      tradeBehaviorProfile: null,
    };
  }

  // Regime: posture 'positive_gamma'|'negative_gamma'|'neutral_gamma' → 'POSITIVE'|'NEGATIVE'|'NEUTRAL'
  let regime: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL' | null = null;
  if (v2?.posture === 'positive_gamma')      regime = 'POSITIVE';
  else if (v2?.posture === 'negative_gamma') regime = 'NEGATIVE';
  else if (v2?.posture === 'neutral_gamma')  regime = 'NEUTRAL';

  // Bias: derived from regimeHint + wall direction asymmetry
  let bias: 'PIN' | 'UPSIDE_EXPANSION' | 'DOWNSIDE_EXPANSION' | 'BALANCED' | null = null;
  const hint = v2?.thesisHints?.regimeHint;
  if (hint === 'pinning')       bias = 'PIN';
  else if (hint === 'balanced') bias = 'BALANCED';
  else if (hint === 'unstable') {
    const above = v2?.thesisHints?.nearestWallAbove;
    const below = v2?.thesisHints?.nearestWallBelow;
    if (below == null && above != null)      bias = 'DOWNSIDE_EXPANSION';
    else if (above == null && below != null) bias = 'UPSIDE_EXPANSION';
    else                                      bias = 'BALANCED';
  }

  // Walls: pick highest-strength resistance for callWall, highest-strength support
  // for putWall, highest-strength magnet (across strongestWalls + voidZones) for
  // keyMagnetStrike. When no support wall exists (v9.0.0 is conservative and
  // only emits support if dealers are positioned to defend), fall back to the
  // strongest void zone below spot — that strike still represents a meaningful
  // dealer-hedging level (usually peak put OI) that traders should watch.
  const walls       = Array.isArray(v2?.strongestWalls) ? v2.strongestWalls : [];
  const voids       = Array.isArray(v2?.voidZones)      ? v2.voidZones      : [];
  const resistances = walls.filter((w: any) => w?.side === 'resistance');
  const supports    = walls.filter((w: any) => w?.side === 'support');
  const magnets     = [...walls, ...voids].filter((w: any) => w?.side === 'magnet');

  const callWall        = resistances.length ? resistances[0].strike : null;
  const keyMagnetStrike = magnets.length     ? magnets[0].strike     : null;

  // Put wall: prefer explicit support wall. Fallback: strongest void zone below spot.
  let putWall: number | null = supports.length ? supports[0].strike : null;
  if (putWall === null && v2?.spot && voids.length > 0) {
    const voidsBelow = voids.filter((v: any) =>
      v?.side === 'void' && typeof v.strike === 'number' && v.strike < v2.spot
    );
    if (voidsBelow.length > 0) {
      // voidZones are already sorted by strength descending in the backend response
      putWall = voidsBelow[0].strike;
    }
  }

  // Scores
  const gammaScore = Number.isFinite(v2?.postureScore) ? v2.postureScore : null;
  const pinScore   = v2?.concentration?.top3Share != null
    ? Math.round(v2.concentration.top3Share * 100) : null;
  const expansionScore = pinScore != null ? Math.max(0, 100 - pinScore) : null;

  let directionalScore: number | null = null;
  if (v2?.spot && v2?.estimatedFlipZone?.low != null && v2?.estimatedFlipZone?.high != null) {
    const flipMid = (v2.estimatedFlipZone.low + v2.estimatedFlipZone.high) / 2;
    directionalScore = Math.min(100, Math.round(Math.abs((v2.spot - flipMid) / v2.spot) * 100 * 5));
  }

  // Flip zone
  const flipZoneLow  = v2?.estimatedFlipZone?.low  ?? v2?.thesisHints?.flipZoneLow  ?? null;
  const flipZoneHigh = v2?.estimatedFlipZone?.high ?? v2?.thesisHints?.flipZoneHigh ?? null;

  // Breakout fuel: fraction of "room" between spot and the nearest wall (0–1).
  // The UI multiplies this value by 100 to display as a percentage, so we emit
  // a fraction here. Clamped to [0, 1]: values > 1 would mean the wall is more
  // than 100% of spot away, which for near-the-money gamma analysis indicates
  // the wall is effectively not binding.
  let breakoutFuelUp:   number | null = null;
  let breakoutFuelDown: number | null = null;
  if (v2?.spot && callWall != null) {
    const room = (callWall - v2.spot) / v2.spot;
    breakoutFuelUp = Math.max(0, Math.min(1, Number(room.toFixed(4))));
  }
  if (v2?.spot && putWall != null) {
    const room = (v2.spot - putWall) / v2.spot;
    breakoutFuelDown = Math.max(0, Math.min(1, Number(room.toFixed(4))));
  }

  // Data quality
  const conf = v2?.confidence ?? v2?.meta?.quoteCoveragePct ?? 0;
  let dataQuality: 'high' | 'medium' | 'low' | null = null;
  if (conf >= 0.85)      dataQuality = 'high';
  else if (conf >= 0.50) dataQuality = 'medium';
  else if (conf > 0)     dataQuality = 'low';

  const notes: string[] = Array.isArray(v2?.riskFlags) ? [...v2.riskFlags] : [];

  return {
    symbol:           v2?.symbol ?? '',
    available:        v2?.available === true,
    reason:           v2?.reason ?? null,
    regime,
    bias,
    gammaScore,
    pinScore,
    expansionScore,
    directionalScore,
    callWall,
    putWall,
    keyMagnetStrike,
    flipZoneLow,
    flipZoneHigh,
    breakoutFuelUp,
    breakoutFuelDown,
    isStale:          v2?.isStale ?? false,
    computedAt:       v2?.computedAt ?? null,
    dataFetchedAt:    v2?.computedAt ?? null,
    dataQuality,
    notes,
    tradeBehaviorProfile: null,
  };
}

// ── Backoff constants ───────────────────────────────────────────────────────

/** Apply backoff after this many consecutive NOT_WIRED responses. */
const BACKOFF_AFTER_N    = 3;
/** Interval multiplier during backoff. */
const BACKOFF_MULTIPLIER = 3;
/** Maximum effective polling interval during backoff (ms). */
const MAX_BACKOFF_MS     = 120_000;   // 2 minutes

// ── Hook ────────────────────────────────────────────────────────────────────

export function useGammaSnapshot(
  symbol: string,
  options: { autoRefreshMs?: number } = {}
): UseGammaSnapshotResult {
  const [loading,  setLoading]  = useState(false);
  const [snapshot, setSnapshot] = useState<LiveGammaSnapshot | null>(null);
  const [error,    setError]    = useState<string | null>(null);

  const abortRef         = useRef<AbortController | null>(null);
  const timerRef         = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notWiredCountRef = useRef(0);
  const destroyedRef     = useRef(false);

  const fetch_ = useCallback(async (): Promise<void> => {
    if (!symbol || destroyedRef.current) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    try {
      const res = await fetch(`/api/gamma/v2/snapshot/${encodeURIComponent(symbol)}`, {
        signal: ctrl.signal, credentials: 'include',
      });
      if (destroyedRef.current) return;

      if (!res.ok) {
        setSnapshot(null);
        setError(`HTTP ${res.status}`);
        notWiredCountRef.current = 0;
        return;
      }

      // v9.0.0 response → map to LiveGammaSnapshot shape
      const raw  = await res.json();
      if (destroyedRef.current) return;
      const data = mapV2ToLiveGammaSnapshot(raw);

      setSnapshot(data);
      setError(null);

      // Track consecutive NOT_WIRED for backoff
      if (!data.available && data.reason === 'NOT_WIRED') {
        notWiredCountRef.current += 1;
      } else {
        notWiredCountRef.current = 0;
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (destroyedRef.current) return;
      setSnapshot(null);
      setError('Gamma snapshot unavailable');
      notWiredCountRef.current = 0;
    } finally {
      if (!destroyedRef.current) setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    // Guard: do nothing when no symbol
    if (!symbol) return;

    const baseMs = options.autoRefreshMs ?? 0;
    destroyedRef.current     = false;
    notWiredCountRef.current = 0;

    setSnapshot(null);
    setError(null);
    fetch_();

    if (baseMs <= 0) {
      return () => {
        destroyedRef.current = true;
        abortRef.current?.abort();
      };
    }

    // Recursive setTimeout loop — safer than setInterval for visibility interplay
    function scheduleNext(): void {
      if (destroyedRef.current) return;
      const applyBackoff = notWiredCountRef.current >= BACKOFF_AFTER_N;
      const intervalMs   = applyBackoff
        ? Math.min(baseMs * BACKOFF_MULTIPLIER, MAX_BACKOFF_MS)
        : baseMs;

      timerRef.current = setTimeout(async () => {
        if (destroyedRef.current) return;
        // Skip tick if tab hidden — listener will re-trigger on visibility
        if (document.visibilityState === 'hidden') {
          scheduleNext();
          return;
        }
        await fetch_();
        scheduleNext();
      }, intervalMs);
    }

    scheduleNext();

    // Resume polling immediately when tab becomes visible
    function onVisibility(): void {
      if (document.visibilityState !== 'visible' || destroyedRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      fetch_().then(scheduleNext);
    }

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      destroyedRef.current = true;
      abortRef.current?.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [symbol, fetch_, options.autoRefreshMs]);

  return { loading, snapshot, error, refresh: fetch_ };
}
